import type { ReactNode } from 'react';

/**
 * Shared layout for the three legal pages (Privacy, Terms, Cookies).
 *
 * Static, print-friendly, no auth required. Rendered in its own browser
 * tab (target="_blank") from the Sign-In page and the footer.
 *
 * Placeholder copy lives in the individual page files — Legal team
 * replaces the body content before go-live. Do NOT ship the "PLACEHOLDER"
 * banner to production without Legal sign-off.
 */
export function LegalShell({
  title,
  effectiveDate,
  children,
}: {
  title: string;
  effectiveDate: string;
  children: ReactNode;
}) {
  return (
    <div className="min-h-screen bg-white text-text-1 font-sans">
      <header className="bg-navy text-white px-6 py-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="/DreamIT_logo.png"
              alt=""
              aria-hidden
              className="h-8 w-auto brightness-0 invert"
              onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
            />
            <span className="font-bold text-sm tracking-wide">DreamTrials</span>
          </div>
          <button
            type="button"
            onClick={() => window.print()}
            className="text-[11px] font-semibold border border-white/40 rounded px-3 py-1 hover:bg-white/10"
          >
            Print
          </button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8 prose prose-sm">
        <p className="text-[11px] uppercase tracking-wider text-text-3 mb-2">Legal</p>
        <h1 className="text-2xl font-bold text-navy mb-1">{title}</h1>
        <p className="text-[11px] text-text-3 mb-6">Effective date: {effectiveDate}</p>
        <div className="text-[13px] leading-6 space-y-4">{children}</div>
      </main>

      <footer className="border-t border-border-strong text-center py-4 text-[10.5px] text-text-4">
        © 2026 DreamTrials · All rights reserved
      </footer>
    </div>
  );
}
